<div class="space-y-4">
  <div class="flex items-end gap-3">
    <div class="flex-1 flex flex-col gap-1">
      <label class="input-label" for="group-papers-search">Search papers</label>
      <input
        id="group-papers-search"
        type="search"
        class="input-field"
        placeholder="Search by title, venue or author…"
        [value]="query()"
        (input)="onQueryChange($any($event.target).value)"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label class="input-label text-xs" for="source-filter">Source</label>
      <select
        id="source-filter"
        class="input-field min-w-[140px]"
        [value]="selectedSource()"
        (change)="onSourceChange($any($event.target).value)"
      >
        @for (opt of SOURCE_OPTIONS; track opt.value) {
          <option [value]="opt.value">{{ opt.label }}</option>
        }
      </select>
    </div>
  </div>

  @if (projects().length > 0) {
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-xs text-slate-500 font-medium">Projects</span>
      @if (selectedProjectIds().length > 0) {
        <button
          type="button"
          class="text-xs text-slate-500 hover:text-slate-700 underline px-2"
          (click)="clearProjects()"
        >
          Clear
        </button>
      }
      <br />
      <div class="flex flex-wrap gap-1">
        @for (project of projects(); track project.id) {
          <button
            type="button"
            class="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
            [class.bg-secondary]="selectedProjectIds().includes(project.id)"
            [class.text-white]="selectedProjectIds().includes(project.id)"
            [class.border-secondary]="selectedProjectIds().includes(project.id)"
            [class.border-slate-300]="!selectedProjectIds().includes(project.id)"
            [class.text-slate-700]="!selectedProjectIds().includes(project.id)"
            (click)="toggleProject(project.id)"
          >
            {{ project.title }}
          </button>
        }
      </div>
    </div>
  }

  @if (loading()) {
    <div class="space-y-3">
      @for (item of [1, 2, 3, 4, 5]; track item) {
        <div class="h-16 rounded-lg bg-slate-100 animate-pulse"></div>
      }
    </div>
  }

  @if (errorMessage(); as msg) {
    <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {{ msg }}
    </div>
  }

  @if (results(); as page) {
    @if (page.items.length === 0) {
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <p class="empty-state-title">No papers found</p>
        <p class="empty-state-message">
          {{
            query() || selectedProjectIds().length > 0 || selectedSource()
              ? 'Try a different search term or adjust the filters.'
              : 'No papers have been uploaded to this group yet.'
          }}
        </p>
      </div>
    } @else {
      <p class="results-counter">
        Showing
        <span class="results-counter-count">{{ page.items.length }}</span>
        of
        <span class="results-counter-count">{{ page.totalItems }}</span>
        paper{{ page.totalItems === 1 ? '' : 's' }}
      </p>

      <div
        class="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white overflow-hidden"
      >
        @for (paper of page.items; track paper.id) {
          <div class="list-item">
            <div class="min-w-0 flex-1">
              <p class="font-medium text-gray-900 truncate" [title]="paper.title">
                {{ paper.title || 'Untitled' }}
              </p>
              <p class="text-sm text-gray-500">
                {{ paper.venue || 'Unknown venue' }}
                @if (paper.year) {
                  <span> · {{ paper.year }}</span>
                }
                <span
                  class="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                >
                  {{ paper.source }}
                </span>
              </p>
            </div>
            <div class="flex items-center gap-3 flex-shrink-0">
              @if (paper.hasPdf) {
                <button type="button" (click)="openPdf(paper)" class="btn-small-text">
                  View PDF
                </button>
              }
              <button type="button" (click)="openDetail(paper.id)" class="btn-small-text">
                View details
              </button>
            </div>
          </div>
        }
      </div>

      @if (page.totalPages > 1) {
        <div class="flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            (click)="goToPage(currentPage() - 1)"
            [disabled]="!canPrev()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            « Prev
          </button>
          <span>Page {{ page.currentPage + 1 }} of {{ page.totalPages }}</span>
          <button
            type="button"
            (click)="goToPage(currentPage() + 1)"
            [disabled]="!canNext()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next »
          </button>
        </div>
      }
    }
  }
</div>

<app-base-modal
  [open]="showDetailModal()"
  (openChange)="showDetailModal.set($event)"
  [title]="selectedPaper()?.title || 'Paper Details'"
  size="xl"
>
  @if (detailLoading()) {
    <div class="flex items-center justify-center py-12">
      <p class="text-gray-500">Loading details…</p>
    </div>
  } @else if (detailError(); as msg) {
    <p class="text-sm text-red-700">{{ msg }}</p>
  } @else if (selectedPaper(); as paper) {
    <div class="space-y-5">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="info-box">
          <p class="info-box-label">Authors</p>
          <p class="info-box-value">{{ paper.authors || 'Unknown' }}</p>
        </div>
        <div class="info-box">
          <p class="info-box-label">Venue</p>
          <p class="info-box-value">{{ paper.venue || 'Unknown' }}</p>
        </div>
        @if (paper.doi) {
          <div class="info-box">
            <p class="info-box-label">DOI</p>
            <a
              [href]="'https://doi.org/' + paper.doi"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-2 block text-sm font-medium text-secondary hover:underline"
            >
              {{ paper.doi }}
            </a>
          </div>
        }
        @if (paper.publicationDate) {
          <div class="info-box">
            <p class="info-box-label">Publication Date</p>
            <p class="info-box-value">{{ paper.publicationDate | date: 'MMM d, yyyy' }}</p>
          </div>
        }
        <div class="info-box">
          <p class="info-box-label">Source</p>
          <p class="info-box-value">{{ paper.source }}</p>
        </div>
      </div>

      @if (paper.abstractText) {
        <div class="info-box">
          <p class="info-box-label">Abstract</p>
          <p class="mt-2 text-sm text-gray-700 leading-relaxed">{{ paper.abstractText }}</p>
        </div>
      }
    </div>
  }
</app-base-modal>

<app-base-modal
  [open]="showPdfModal()"
  (openChange)="closePdf()"
  [title]="'PDF Viewer — ' + selectedPdfTitle()"
  size="2xl"
>
  @if (pdfLoading()) {
    <div
      class="flex h-[84vh] items-center justify-center rounded-lg border border-slate-200 text-sm text-slate-600"
    >
      Loading PDF...
    </div>
  } @else if (pdfViewerUrl(); as viewerUrl) {
    <iframe
      class="h-[84vh] w-full rounded-lg border border-slate-200"
      [src]="viewerUrl"
      title="Paper viewer"
    ></iframe>
  } @else if (pdfError()) {
    <div
      class="flex h-[84vh] items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800"
    >
      {{ pdfError() }}
    </div>
  }
</app-base-modal>
